/**
 * 最小ビルダー core（取り込み後の編集・公開のための純関数群）
 * -----------------------------------------------------------------------------
 * すべて immutable（structuredClone してから変更）。UI からもテストからも使える。
 * 編集のたびに version を単調増加させ（schema の契約）、updatedAt を更新する。
 *
 * 公開モデル（RISK B-4 / APP-DEFINITION-MODEL）:
 *  - draft = 可変。編集はここに対して行う（status: "draft"）。
 *  - publish = draft を検証（validate-app）→ 合格したら immutable スナップショット
 *    （status: "published"・deepFreeze）を返す。draft 自体は変更しない。
 *  - 公開スナップショットの採番・保存は app-store.mjs が担当（provenance = app.version）。
 */
import { validateAppDefinition } from "./validate-app.mjs";

const clone = (x) => structuredClone(x);
const nowIso = () => new Date().toISOString();

/** 編集を確定: version を +1 し updatedAt を更新（schema「編集のたびに単調増加」）。 */
function commit(app) {
  app.version = (Number(app.version) || 0) + 1;
  app.updatedAt = nowIso();
  app.status = "draft";
  return app;
}

function findView(app, viewId) {
  const v = app.views.find((x) => x.id === viewId);
  if (!v) throw new Error(`view が見つかりません: ${viewId}`);
  return v;
}

// ---- アプリ全体 -----------------------------------------------------------
export function renameApp(app, name) {
  const a = clone(app);
  a.name = typeof name === "string" ? { ...a.name, ja: name } : { ...a.name, ...name };
  return commit(a);
}

export function setHomeView(app, viewId) {
  findView(app, viewId); // 存在チェック
  const a = clone(app);
  a.settings = { ...a.settings, homeViewId: viewId };
  return commit(a);
}

export function setBrandBase(app, base) {
  if (!["light", "dark", "auto"].includes(base)) throw new Error(`不正な base: ${base}`);
  const a = clone(app);
  a.brand = { ...a.brand, base };
  return commit(a);
}

// ---- ビューのブロック操作 --------------------------------------------------
/** ブロックを上下に移動（表示順の変更）。無効な移動は元の app を返す（no-op）。 */
export function moveBlock(app, viewId, blockId, dir) {
  const v0 = findView(app, viewId);
  const blocks = v0.layout.blocks;
  const i = blocks.findIndex((b) => b.id === blockId);
  const j = dir === "up" ? i - 1 : i + 1;
  if (i < 0 || j < 0 || j >= blocks.length) return app; // no-op
  const a = clone(app);
  const bl = findView(a, viewId).layout.blocks;
  [bl[i], bl[j]] = [bl[j], bl[i]];
  return commit(a);
}

export function removeBlock(app, viewId, blockId) {
  const a = clone(app);
  const v = findView(a, viewId);
  const before = v.layout.blocks.length;
  v.layout.blocks = v.layout.blocks.filter((b) => b.id !== blockId);
  if (v.layout.blocks.length === before) return app; // no-op
  return commit(a);
}

/** 列に束縛した field ブロックを末尾に追加（フル幅）。id は列名ベース、衝突時は連番。 */
export function addFieldBlock(app, viewId, columnId, idHint) {
  const a = clone(app);
  const v = findView(a, viewId);
  const table = a.tables.find((t) => t.id === v.tableId);
  if (!table || !table.columns.some((c) => c.id === columnId)) {
    throw new Error(`列が見つかりません: ${columnId}`);
  }
  let id = idHint || `b-${columnId}`;
  let n = 2;
  const used = new Set(v.layout.blocks.map((b) => b.id));
  while (used.has(id)) id = `${idHint || `b-${columnId}`}-${n++}`;
  v.layout.blocks.push({
    id,
    component: "field",
    binding: { columnId },
    position: { colStart: 1, colSpan: v.layout.grid.columns },
  });
  return commit(a);
}

/** style をマージ（部分更新）。patch に null を渡すとそのキーを削除。 */
export function updateBlockStyle(app, viewId, blockId, patch) {
  const a = clone(app);
  const b = findView(a, viewId).layout.blocks.find((x) => x.id === blockId);
  if (!b) return app;
  const style = { ...(b.style || {}) };
  for (const [k, val] of Object.entries(patch)) {
    if (val == null) delete style[k];
    else style[k] = val;
  }
  b.style = style;
  return commit(a);
}

/** colSpan を 1..grid.columns にクランプして設定。 */
export function setBlockSpan(app, viewId, blockId, colSpan) {
  const a = clone(app);
  const v = findView(a, viewId);
  const b = v.layout.blocks.find((x) => x.id === blockId);
  if (!b) return app;
  const max = v.layout.grid.columns;
  const span = Math.max(1, Math.min(max, Math.round(Number(colSpan) || 1)));
  // colStart がはみ出すなら colStart も引き戻す（B-5 はみ出し防止）
  const colStart = Math.min(b.position.colStart, max - span + 1);
  b.position = { ...b.position, colStart: Math.max(1, colStart), colSpan: span };
  return commit(a);
}

/**
 * ブロックの位置を設定（ドラッグ配置の commit 先）。
 * B-5 の安全上限にクランプ: colSpan 1..cols / colStart は右端はみ出し禁止 / row は 1 以上。
 */
export function setBlockPosition(app, viewId, blockId, patch) {
  const a = clone(app);
  const v = findView(a, viewId);
  const b = v.layout.blocks.find((x) => x.id === blockId);
  if (!b) return app;
  const cols = v.layout.grid.columns;
  const cur = b.position;
  const colSpan = Math.max(1, Math.min(cols, Math.round(patch.colSpan ?? cur.colSpan)));
  const colStart = Math.max(1, Math.min(cols - colSpan + 1, Math.round(patch.colStart ?? cur.colStart)));
  const rowSpan = Math.max(1, Math.round(patch.rowSpan ?? cur.rowSpan ?? 1));
  const rowStart = Math.max(1, Math.round(patch.rowStart ?? cur.rowStart ?? 1));
  b.position = { colStart, colSpan, rowStart, rowSpan };
  return commit(a);
}

/**
 * 位置の正規化（ドラッグ編集の前処理）。rowStart を持たないブロックに、
 * レンダラの列ベース自動配置（sparse first-fit）を再現して rowStart を割り当て、
 * すべてのブロックに colStart/colSpan/rowStart/rowSpan を明示する。
 * canonicalization（ユーザー編集ではない）なので version は上げない。
 * 変化がなければ元の app をそのまま返す（べき等・参照安定）。
 */
export function normalizePositions(app, viewId) {
  const view = app.views.find((x) => x.id === viewId);
  if (!view) return app;
  const cols = view.layout.grid.columns;
  const occ = []; // occ[row-1][col] = 使用中
  const ensureRow = (r) => { while (occ.length < r) occ.push(new Array(cols + 1).fill(false)); };
  const fits = (r, cs, span) => {
    ensureRow(r);
    for (let c = cs; c < cs + span; c++) { if (c > cols || occ[r - 1][c]) return false; }
    return true;
  };
  const mark = (r, cs, span, rspan) => {
    for (let rr = r; rr < r + rspan; rr++) { ensureRow(rr); for (let c = cs; c < cs + span; c++) occ[rr - 1][c] = true; }
  };

  let changed = false;
  const next = view.layout.blocks.map((b) => {
    const p = b.position || {};
    const colSpan = Math.max(1, Math.min(cols, p.colSpan || 1));
    const colStart = Math.max(1, Math.min(cols - colSpan + 1, p.colStart || 1));
    const rowSpan = Math.max(1, p.rowSpan || 1);
    let rowStart = p.rowStart;
    if (rowStart == null) { rowStart = 1; while (!fits(rowStart, colStart, colSpan)) rowStart++; }
    mark(rowStart, colStart, colSpan, rowSpan);
    if (p.colStart !== colStart || p.colSpan !== colSpan || p.rowStart !== rowStart || p.rowSpan !== rowSpan) changed = true;
    return { ...b, position: { colStart, colSpan, rowStart, rowSpan } };
  });
  if (!changed) return app;
  const a = clone(app);
  a.views.find((x) => x.id === viewId).layout.blocks = next.map((b) => structuredClone(b));
  return a;
}

/** 2つのブロック位置（グリッド矩形）が重なるか。rowStart 未確定は判定不能=false。 */
export function blocksOverlap(p, q) {
  if (p.rowStart == null || q.rowStart == null) return false;
  const ax2 = p.colStart + p.colSpan - 1, ay2 = p.rowStart + (p.rowSpan || 1) - 1;
  const bx2 = q.colStart + q.colSpan - 1, by2 = q.rowStart + (q.rowSpan || 1) - 1;
  return p.colStart <= bx2 && q.colStart <= ax2 && p.rowStart <= by2 && q.rowStart <= ay2;
}

/**
 * ビュー内のブロック重なりを検出（同一グリッドの兄弟同士・container 内も再帰）。
 * 位置を正規化してから判定するので、auto-flow（rowStart 無し）由来の見かけの重なりは出さず、
 * ドラッグで明示配置した本当の重なりだけを返す。
 * @returns {Array<{a:Id, b:Id}>} 重なっているブロックIDのペア
 */
export function findOverlaps(app, viewId) {
  const n = normalizePositions(app, viewId);
  const view = n.views.find((v) => v.id === viewId);
  if (!view) return [];
  const pairs = [];
  const scan = (blocks) => {
    for (let i = 0; i < blocks.length; i++) {
      for (let j = i + 1; j < blocks.length; j++) {
        if (blocksOverlap(blocks[i].position, blocks[j].position)) pairs.push({ a: blocks[i].id, b: blocks[j].id });
      }
    }
    for (const b of blocks) if (b.children?.length) scan(b.children);
  };
  scan(view.layout.blocks);
  return pairs;
}


// ---- スキーマ編集（列・ビュー・テーマ） -----------------------------------
/** テーブルに列を追加。id 重複は例外。 */
export function addColumn(app, tableId, col) {
  if (!col?.id) throw new Error("col.id が必要です");
  const a = clone(app);
  const t = a.tables.find((x) => x.id === tableId);
  if (!t) throw new Error(`table が見つかりません: ${tableId}`);
  if (t.columns.some((c) => c.id === col.id)) throw new Error(`列 id が重複: ${col.id}`);
  t.columns.push({
    id: col.id,
    sourceRef: col.sourceRef ?? col.id,
    name: col.name ?? { ja: col.id },
    type: col.type ?? "text",
    ...(col.options ? { options: col.options } : {}),
  });
  return commit(a);
}

/** ビューを追加（空レイアウト）。id 重複・tableId 不在は例外。nav にも追加。 */
export function addView(app, spec) {
  if (!spec?.id) throw new Error("view.id が必要です");
  const a = clone(app);
  if (a.views.some((v) => v.id === spec.id)) throw new Error(`view id が重複: ${spec.id}`);
  if (!a.tables.some((t) => t.id === spec.tableId)) throw new Error(`tableId が存在しません: ${spec.tableId}`);
  const name = spec.name ?? { ja: spec.id };
  a.views.push({
    id: spec.id,
    name,
    type: spec.type ?? "list",
    tableId: spec.tableId,
    layout: {
      kind: "blocks",
      grid: { columns: spec.columns ?? 6, gap: "space.2" },
      breakpoints: [{ maxWidth: 480, columns: 2 }],
      blocks: [],
    },
  });
  a.navigation = [...(a.navigation ?? []), { id: `nav-${spec.id}`, label: name, viewId: spec.id }];
  return commit(a);
}

/** 列を削除。主キーは削除不可。その列に束縛された全ビューのブロックも除去。 */
export function removeColumn(app, tableId, colId) {
  const a = clone(app);
  const t = a.tables.find((x) => x.id === tableId);
  if (!t) throw new Error(`table が見つかりません: ${tableId}`);
  if (t.primaryKey === colId) throw new Error(`主キー列は削除できません: ${colId}`);
  const before = t.columns.length;
  t.columns = t.columns.filter((c) => c.id !== colId);
  if (t.columns.length === before) return app; // no-op
  // この table を使うビューから、束縛ブロックを除去
  for (const v of a.views) {
    if (v.tableId !== tableId) continue;
    const strip = (blocks) => blocks.filter((b) => {
      if (b.children) b.children = strip(b.children);
      return b.binding?.columnId !== colId && b.chart?.groupByColumnId !== colId && b.chart?.valueColumnId !== colId;
    });
    v.layout.blocks = strip(v.layout.blocks);
  }
  return commit(a);
}

/** ビューを削除。最後の1ビューは残す。nav・home も整合させる。 */
export function removeView(app, viewId) {
  const a = clone(app);
  if (a.views.length <= 1) throw new Error("最後のビューは削除できません");
  if (!a.views.some((v) => v.id === viewId)) return app;
  a.views = a.views.filter((v) => v.id !== viewId);
  a.navigation = (a.navigation ?? []).filter((n) => n.viewId !== viewId);
  if (a.settings?.homeViewId === viewId) a.settings = { ...a.settings, homeViewId: a.views[0].id };
  return commit(a);
}

/** グラフブロックを追加（全行集計の棒グラフ）。 */
export function addChartBlock(app, viewId, spec, idHint) {
  const a = clone(app);
  const v = findView(a, viewId);
  let id = idHint || "b-chart";
  let n = 2; const used = new Set(v.layout.blocks.map((b) => b.id));
  while (used.has(id)) id = `${idHint || "b-chart"}-${n++}`;
  v.layout.blocks.push({
    id,
    component: "chart",
    chart: { kind: "bar", agg: "count", ...(spec || {}) },
    position: { colStart: 1, colSpan: v.layout.grid.columns },
  });
  return commit(a);
}

const KNOWN_VIEW_TYPES = new Set(["list", "table", "detail", "form", "board", "gallery", "calendar", "map", "dashboard"]);

/** 既存ビューのタイプを変更（AppSheet の View type 相当）。 */
export function setViewType(app, viewId, type) {
  if (!KNOWN_VIEW_TYPES.has(type)) throw new Error(`未知の view type: ${type}`);
  const a = clone(app);
  findView(a, viewId).type = type;
  return commit(a);
}

/**
 * ビューの並び替えを設定（Sort by）。sort=[{columnId, dir}]。空配列/undefined で解除。
 * 列の存在を検証。
 */
export function setViewSort(app, viewId, sort) {
  const a = clone(app);
  const v = findView(a, viewId);
  const table = a.tables.find((t) => t.id === v.tableId);
  const colIds = new Set((table?.columns ?? []).map((c) => c.id));
  const clean = (sort ?? []).filter((s) => colIds.has(s.columnId)).map((s) => ({ columnId: s.columnId, dir: s.dir === "desc" ? "desc" : "asc" }));
  if (clean.length) v.sort = clean; else delete v.sort;
  return commit(a);
}

/**
 * グループ化と集計を設定（Group by / Group aggregate）。
 * groupByColumnId=null で解除。agg="count"|"sum"（sum は valueColumnId 必須）。
 */
export function setViewGroup(app, viewId, { groupByColumnId, agg, valueColumnId } = {}) {
  const a = clone(app);
  const v = findView(a, viewId);
  const table = a.tables.find((t) => t.id === v.tableId);
  const colIds = new Set((table?.columns ?? []).map((c) => c.id));
  if (!groupByColumnId || !colIds.has(groupByColumnId)) {
    delete v.groupByColumnId; delete v.groupAgg;
  } else {
    v.groupByColumnId = groupByColumnId;
    if (agg === "sum" && valueColumnId && colIds.has(valueColumnId)) v.groupAgg = { agg: "sum", columnId: valueColumnId };
    else v.groupAgg = { agg: "count" };
  }
  return commit(a);
}

/**
 * table ビューの表示列・順序・幅を設定（AppSheet の Column order / width 相当 / D-0028）。
 * specs=[{columnId, width?}] の順序が表示順。存在しない列は除去。空/null で「全列・既定」に戻す。
 */
export function setTableColumns(app, viewId, specs) {
  const a = clone(app);
  const v = findView(a, viewId);
  const table = a.tables.find((t) => t.id === v.tableId);
  const colIds = new Set((table?.columns ?? []).map((c) => c.id));
  const clean = (specs ?? [])
    .filter((s) => colIds.has(s.columnId))
    .map((s) => ({ columnId: s.columnId, ...(s.width && s.width !== "default" ? { width: s.width } : {}) }));
  if (clean.length) v.tableColumns = clean; else delete v.tableColumns;
  return commit(a);
}

/** QuickEdit（一覧で直接編集）の ON/OFF（D-0028）。 */
export function setQuickEdit(app, viewId, on) {
  const a = clone(app);
  const v = findView(a, viewId);
  if (on) v.quickEdit = true; else delete v.quickEdit;
  return commit(a);
}

/** calendar ビューの日付列を設定（null で解除）。 */
export function setViewDateColumn(app, viewId, columnId) {
  const a = clone(app);
  const v = findView(a, viewId);
  if (columnId) v.dateColumnId = columnId; else delete v.dateColumnId;
  return commit(a);
}

/** map ビューの位置列を設定（null で解除）。 */
export function setViewLocationColumn(app, viewId, columnId) {
  const a = clone(app);
  const v = findView(a, viewId);
  if (columnId) v.locationColumnId = columnId; else delete v.locationColumnId;
  return commit(a);
}

/** ブランドのデザイントークンを上書き（value=null で削除）。テーマ編集。 */
export function setBrandToken(app, key, value) {
  const a = clone(app);
  const tokens = { ...(a.brand?.tokens ?? {}) };
  if (value == null || value === "") delete tokens[key];
  else tokens[key] = value;
  a.brand = { ...a.brand, tokens };
  return commit(a);
}

// ---- 公開 -----------------------------------------------------------------
function deepFreeze(o) {
  if (o && typeof o === "object") {
    Object.values(o).forEach(deepFreeze);
    Object.freeze(o);
  }
  return o;
}

/**
 * draft を検証して公開スナップショットを作る。
 * @returns {{ok:true, snapshot:object, warnings:string[]} | {ok:false, errors:string[], warnings:string[]}}
 */
export function publish(app) {
  const r = validateAppDefinition(app);
  if (!r.ok) return { ok: false, errors: r.errors, warnings: r.warnings };
  const snap = clone(app);
  snap.status = "published";
  snap.updatedAt = nowIso();
  // version は provenance（どの draft version から公開したか）としてそのまま保持。
  deepFreeze(snap);
  return { ok: true, snapshot: snap, warnings: r.warnings };
}
